import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TotalRewardsConfig } from "src/Pages/Company/TotalReward/models/TotalRewardsConfig.models";
import {
  createConfig,
  deleteConfig,
  editConfig,
  fetchConfig,
  fetchConfigs,
  fetchConfigsDictionary,
} from ".";

const parseConfigValue = (value: string, type: string) => {
  if (type === "boolean") {
    return value === "1";
  }
  if (type === "autocompleteMultiselect") {
    return value.split(",");
  }
  return value;
};

export const useGetTotalRewardsConfigs = (companyId: string) => {
  const query = useQuery({
    queryKey: ["total-rewards-config", "list", { companyId }],
    queryFn: async ({ signal }) => fetchConfigs(companyId, { signal }),
    staleTime: Infinity,
  });
  const getConfigValue = (key: string, defaultValue: unknown) => {
    const config = query.data?.find((item) => item.configKey === key);
    return config
      ? parseConfigValue(config.configValue, config.valueType)
      : defaultValue;
  };
  return { ...query, getConfigValue };
};

export const useListTotalRewardsConfigsDictionary = (companyId: string) =>
  useQuery({
    queryKey: ["total-rewards-config-dictionary", "list", { companyId }],
    queryFn: async ({ signal }) =>
      fetchConfigsDictionary(companyId, { signal }),
  });

export const useListTotalRewardsConfigs = (companyId: string) =>
  useQuery({
    queryKey: ["total-rewards-config", "list", { companyId }],
    queryFn: async ({ signal }) => fetchConfigs(companyId, { signal }),
  });

export const useGetTotalRewardsConfig = (id: string, companyId: string) =>
  useQuery({
    enabled: id !== "-1",
    queryKey: ["total-rewards-config", "details", { id, companyId }],
    queryFn: async ({ signal }) => fetchConfig(id, companyId, { signal }),
  });

export const useSaveTotalRewardsConfig = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...formData }: TotalRewardsConfig) => {
      if (id) {
        if (formData.configValue !== null) {
          return editConfig(id, formData, companyId);
        }
        return deleteConfig(id, companyId);
      }
      return createConfig(formData, companyId);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<TotalRewardsConfig[]>(
        ["total-rewards-config", "list", { companyId }],
        (oldData) => {
          if (Array.isArray(oldData)) {
            const newData = [...oldData];
            if (variables.id) {
              if (variables.configValue !== null) {
                return newData.map((row) =>
                  row.id === variables.id ? (data as TotalRewardsConfig) : row
                );
              }
              return newData.filter((row) => row.id !== variables.id);
            }
            newData.push(data as TotalRewardsConfig);
            return newData;
          }
          return oldData;
        }
      );
    },
  });
};
